import React, { useState } from "react";

import LoginInput from "../../components/Input/Login";
import List from "../../components/List";

type Props = { fieldName?: string };

export type PasswordConfirmData = {
  password: string;
};

export default function usePasswordConfirm({
  fieldName = "비밀번호",
}: Props): [
  { isComplete: boolean; data: PasswordConfirmData; validate: () => boolean },
  React.ReactNode
] {
  const [data, setData] = useState<PasswordConfirmData>({ password: "" });
  const [passwordConfirm, setPasswordConfirm] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [passwordConfirmError, setPasswordConfirmError] = useState<string>("");
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const passwordConfirmRef = React.useRef<HTMLInputElement>(null);
  const PASSWORD_REGEX = /^[a-zA-Z0-9`~!@#$%^*+=-_]*$/;

  const handleChangeData = (key: string, value: any) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (data.password === "") {
      setPasswordError(`${fieldName}를 입력해주세요.`);
      passwordRef.current?.focus();
      return false;
    }
    if (data.password.length < 8) {
      setPasswordError(`${fieldName}는 8자 이상이어야 합니다.`);
      passwordRef.current?.focus();
      return false;
    }
    if (data.password.length > 13) {
      setPasswordError(`${fieldName}는 13자 이하이어야 합니다.`);
      passwordRef.current?.focus();
      return false;
    }
    if (PASSWORD_REGEX.test(data.password) === false) {
      setPasswordError(
        "영문 대/소문자, 숫자, 특수문자(`~!@#$%^*+=-_만 허용) 조합으로 입력해주세요."
      );
      passwordRef.current?.focus();
      return false;
    }
    if (passwordConfirm === "") {
      setPasswordConfirmError(`${fieldName}를 입력해주세요.`);
      passwordConfirmRef.current?.focus();
      return false;
    }
    if (PASSWORD_REGEX.test(passwordConfirm) === false) {
      setPasswordConfirmError(
        "영문 대/소문자, 숫자, 특수문자(`~!@#$%^*+=-_만 허용) 조합으로 입력해주세요."
      );
      passwordRef.current?.focus();
      return false;
    }
    if (data.password !== passwordConfirm) {
      setPasswordConfirmError("새로운 비밀번호가 일치하지 않습니다.");
      passwordConfirmRef.current?.focus();
      return false;
    }
    return true;
  };

  const validatePassword = (text: string, target: string) => {
    const isEqual = text === "" || text === target;
    const message = isEqual ? "" : "새로운 비밀번호가 일치하지 않습니다.";

    setPasswordConfirmError(message);
    return isEqual;
  };

  return [
    {
      isComplete: data.password === passwordConfirm,
      data,
      validate,
    },
    <>
      <div className="flex flex-col">
        <div className="py-2.5 text-sm text-brand-neutral-60">{fieldName}</div>
        <LoginInput
          placeholder={`영문 대/소문자,숫자,특수문자 8-13자 입력`}
          value={data.password}
          onChange={(text) => {
            if (PASSWORD_REGEX.test(text) === false) {
              setPasswordError(
                "영문 대/소문자, 숫자, 특수문자(`~!@#$%^*+=-_만 허용) 조합으로 입력해주세요."
              );
            } else {
              handleChangeData("password", text);
              setPasswordError("");
              validatePassword(text, passwordConfirm);
            }
          }}
          type="password"
          autoComplete="current-password"
          error={passwordError}
          className="rounded-[3px]"
          outRef={passwordRef}
        />
        {!passwordError && (
          <div className="text-brand-neutral-60 text-xs px-1 pt-1">
            8-13자 영문 대/소문자, 숫자,특수문자(`~!@#$%^*+=-_만 허용) 조합으로
            입력해주세요.
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <div className="py-2.5 text-sm text-brand-neutral-60">
          {`${fieldName} 확인`}
        </div>

        <LoginInput
          placeholder={`영문 대/소문자,숫자,특수문자 8-13자 입력`}
          value={passwordConfirm}
          onChange={(text) => {
            if (PASSWORD_REGEX.test(text) === false) {
              setPasswordError(
                "영문 대/소문자, 숫자, 특수문자(`~!@#$%^*+=-_만 허용) 조합으로 입력해주세요."
              );
            } else {
              setPasswordConfirm(text);
              validatePassword(text, data.password);
            }
          }}
          type="password"
          className="rounded-[3px]"
          error={passwordConfirmError}
          outRef={passwordConfirmRef}
        />
      </div>
      <div className="p-5 flex flex-col gap-[5px] bg-brand-weather-badge-background rounded">
        <div className="text-sm font-medium">
          보안에 취약한 비밀번호는 사용하지 마세요!
        </div>
        <List
          className="text-xs text-brand-neutral-80"
          items={[
            { text: "자주 쓰는 사이트의 비밀번호가 같은 경우" },
            { text: "과거에 사용했던 비밀번호를 다시 사용하는 경우" },
            {
              text: "아이디와 주민등록번호, 생일 등 유추하기 쉬운 비밀번호",
            },
            {
              text: "연속된 숫자, 반복된 문자 등 쉽게 알아낼 수 있는 비밀번호",
            },
          ]}
        />
      </div>
    </>,
  ];
}
